class ProductSelectionProducts extends HTMLElement {
  connectedCallback() {
    if (this.resizeObserver) return;

    this.desktop = window.matchMedia('(min-width: 1184px)');
    this.observedElements = new Set();
    this.observedSizes = new WeakMap();
    this.scheduleAlignment = () => {
      if (this.alignmentFrame) return;
      this.alignmentFrame = requestAnimationFrame(() => {
        this.alignmentFrame = null;
        this.alignRows();
      });
    };
    this.resizeObserver = new ResizeObserver((entries) => {
      let changed = false;
      for (const { target, contentRect } of entries) {
        const previous = this.observedSizes.get(target);
        this.observedSizes.set(target, { width: contentRect.width, height: contentRect.height });
        if (!previous || previous.width !== contentRect.width
          || (target !== this && previous.height !== contentRect.height)) changed = true;
      }
      if (changed) this.scheduleAlignment();
    });
    this.resizeObserver.observe(this);
    this.mutationObserver = new MutationObserver(() => {
      this.observeRows();
      this.scheduleAlignment();
    });
    this.mutationObserver.observe(this, { childList: true, characterData: true, subtree: true });
    this.desktop.addEventListener('change', this.scheduleAlignment);
    this.observeRows();
    this.scheduleAlignment();
    document.fonts.ready.then(() => {
      if (this.isConnected) this.scheduleAlignment();
    });
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.alignmentFrame);
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.desktop?.removeEventListener('change', this.scheduleAlignment);
    this.resizeObserver = null;
    this.alignmentFrame = null;
  }

  observeRows() {
    const elements = new Set(this.querySelectorAll(
      '.product-selection-card__tag-list, .product-selection-card__title, '
      + '.product-selection-card__rating, .product-selection-card__description > *, '
      + '.product-selection-card__description, .product-selection-card__price-container, '
      + '.product-selection-card__buttons'
    ));
    for (const element of this.observedElements) {
      if (!elements.has(element)) this.resizeObserver.unobserve(element);
    }
    for (const element of elements) {
      if (!this.observedElements.has(element)) this.resizeObserver.observe(element);
    }
    this.observedElements = elements;
  }

  alignRows() {
    const cards = Array.from(this.children).filter((element) => element.matches('.product-selection-card'));
    const properties = ['tag-rows', 'details-rows', 'description-rows', 'footer-rows', 'footer-gap'];
    for (const card of cards) {
      card.classList.remove('product-selection-card--aligned');
      for (const property of properties) card.style.removeProperty(`--selection-${property}`);
    }
    if (!this.desktop.matches || cards.length % 2 !== 0 || !this.getBoundingClientRect().width) return;

    const height = (element) => element?.getBoundingClientRect().height || 0;
    const measurements = [];
    for (let index = 0; index + 1 < cards.length; index += 2) {
      const pair = cards.slice(index, index + 2);
      const elements = (name) => pair.map((card) => card.querySelector(`.product-selection-card__${name}`));
      const tallest = (name) => Math.max(...elements(name).map(height));
      const tagHeight = tallest('tag-list');
      const titleHeight = tallest('title');
      const tagGap = tagHeight && titleHeight ? 4 : 0;
      const headingHeight = tagHeight + tagGap + titleHeight;
      const ratingHeight = tallest('rating');
      const descriptions = elements('description').map((element) => {
        if (!element) return [];
        return element.children.length ? Array.from(element.children) : (element.textContent.trim() ? [element] : []);
      });
      const descriptionHeights = Array.from({ length: Math.max(...descriptions.map((rows) => rows.length)) }, (_, row) => (
        Math.max(...descriptions.map((rows) => height(rows[row])))
      ));
      const hasDescription = descriptionHeights.some(Boolean);
      const priceHeight = tallest('price-container');
      const buttonHeight = tallest('buttons');
      measurements.push({ pair, values: {
        'tag-rows': `${tagHeight}px ${tagGap}px ${titleHeight}px`,
        'details-rows': `${headingHeight}px ${headingHeight && ratingHeight ? 8 : 0}px ${ratingHeight}px ${hasDescription && (headingHeight || ratingHeight) ? 8 : 0}px auto`,
        'description-rows': descriptionHeights.length ? descriptionHeights.map((value) => `${value}px`).join(' ') : '0px',
        'footer-rows': `${priceHeight}px ${buttonHeight}px`,
        'footer-gap': `${priceHeight && buttonHeight ? 16 : 0}px`,
      } });
    }
    for (const { pair, values } of measurements) {
      for (const card of pair) {
        for (const [property, value] of Object.entries(values)) card.style.setProperty(`--selection-${property}`, value);
        card.classList.add('product-selection-card--aligned');
      }
    }
  }
}

if (!customElements.get('product-selection-products')) {
  customElements.define('product-selection-products', ProductSelectionProducts);
}
